import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { ApiBaseService } from "./api-base.service";

import { PricingServiceApi } from "@aex/shared/apis";

import {
	EnlistServiceByPromoIdRequestDto,
	AreaSearchRequestDto,
	BillingRuleRequestDto,
	BulkCustomerResponseDto,
	IPromoAppliedBy,
	IPromoBillingFrequency,
	IPromoBillingRule,
	IPromoBillingStatus,
	IPromoCategory,
	IPromoCodeUsage,
	IPromoPeriodType,
	IPromoPriceReduction,
	IPromoReductionType,
	IPromoRemovalReason,
	IPromoServiceStatus,
	IPromotionDiscount,
	IPromotionDiscountForService,
	ProductSearchRequestDto,
	PromoAreaTreeItem,
	PromoDiscountBulkCustomerRequestDto,
	PromoDiscountDataExportRequestDto,
	PromoDiscountDataItem,
	PromoDiscountDeleteRequestDto,
	PromoDiscountFilterListRequestDto,
	PromoDiscountItem,
	PromoDiscountSearchRequestDto,
	PromoProductItem,
	PromoReportSearchRequestDto,
	PromoReportSummaryDataExportRequestDto,
	PromoServicePurchaseInfoDto,
	PromoSummaryDto,
	PromoSummaryServiceDto,
	PromoSummaryServicePurchaseDto,
	PromoSummaryWithDataDto,
	RemovePromoServiceRequestDto,
	EligibilityResponseDto,
} from "@aex/shared/common-lib";
import { IPagedResponse } from "@aex/ngx-toolbox";
import { instanceToPlain } from "class-transformer";

const PRICING_SERVICE_GENERAL_ERROR_TEXT = 'pricingService';

@Injectable({
	providedIn: 'root',
})
export class PricingServiceApiService extends ApiBaseService {

	constructor(http: HttpClient) {
		super(http);
	}

	protected override getHandlerErrorText(): string {
		return PRICING_SERVICE_GENERAL_ERROR_TEXT;
	}

	// Get Promo Price Reductions List
	public getPriceReductionListForPromo(): Observable<IPromoPriceReduction[]> {
		const url = `${PricingServiceApi.promoDiscountTypeList()}/promo`;
		return this.get<IPromoPriceReduction[]>(url);
	}

	public getPriceReductionListForBoltOn(): Observable<IPromoPriceReduction[]> {
		const url = `${PricingServiceApi.promoDiscountTypeList()}/bolt-on`;
		return this.get<IPromoPriceReduction[]>(url);
	}

	// Get Promo Billing Frequency list / Price Type List
	public getBillingFrequencyListForPromo(): Observable<IPromoBillingFrequency[]> {
		const url = `${PricingServiceApi.promoPriceTypeList()}/promo`;
		return this.get<IPromoBillingFrequency[]>(url);
	}

	public getBillingFrequencyListForBoltOn(): Observable<IPromoBillingFrequency[]> {
		const url = `${PricingServiceApi.promoPriceTypeList()}/bolt-on`;
		return this.get<IPromoBillingFrequency[]>(url);
	}

	// Get Promo Category list
	public getCategoryListForPromo(): Observable<IPromoCategory[]> {
		const url = `${PricingServiceApi.promoCategoryList()}/promo`;
		return this.get<IPromoCategory[]>(url);
	}

	public getCategoryListForBoltOn(): Observable<IPromoCategory[]> {
		const url = `${PricingServiceApi.promoCategoryList()}/bolt-on`;
		return this.get<IPromoCategory[]>(url);
	}

	// Get Promo Reduction Type list
	public getReductionTypeListForPromo(): Observable<IPromoReductionType[]> {
		const url = `${PricingServiceApi.promoReductionTypeList()}/promo`;
		return this.get<IPromoReductionType[]>(url);
	}
	
	public getReductionTypeListForBoltOn(): Observable<IPromoReductionType[]> {
		const url = `${PricingServiceApi.promoReductionTypeList()}/bolt-on`;
		return this.get<IPromoReductionType[]>(url);
	}

	// Get Promo Period Type list
	public getPromoPeriodTypeList(): Observable<IPromoPeriodType[]> {
		const url = `${PricingServiceApi.promoPeriodTypeList()}`;
		return this.get<IPromoPeriodType[]>(url);
	}

	// Get Promo Code Usage list
	public getPromoCodeUsageList(): Observable<IPromoCodeUsage[]> {
		const url = `${PricingServiceApi.promoCodeUsageList()}`;
		return this.get<IPromoCodeUsage[]>(url);
	}

	// Get Promo Applied By list
	public getPromoAppliedByList(): Observable<IPromoAppliedBy[]> {
		const url = `${PricingServiceApi.promoAppliedByList()}`;
		return this.get<IPromoAppliedBy[]>(url);
	}

	public getPromoAreaList(areaSearchRequestDto : AreaSearchRequestDto): Observable<PromoAreaTreeItem[]> {
		const url = `${PricingServiceApi.promoAreaTreeList()}/get`;
		return this.post<AreaSearchRequestDto, PromoAreaTreeItem[]>(
			url,
			areaSearchRequestDto,
		);
	}

	public getPromoProductList(productSearchRequestDto : ProductSearchRequestDto): Observable<PromoProductItem[]> {
		const url = `${PricingServiceApi.promoProductList()}/get-products`;
		return this.post<ProductSearchRequestDto, PromoProductItem[]>(
			url,
			productSearchRequestDto,
		);
	}

	public getPromoBillingRuleList(billingRuleRequestDto : BillingRuleRequestDto): Observable<IPromoBillingRule[]> {
		const url = `${PricingServiceApi.promoBillingRuleList()}/get`;
		return this.post<BillingRuleRequestDto, IPromoBillingRule[]>(
			url,
			billingRuleRequestDto,
		);
	}

	public getPromotionDiscountData(searchRequest: PromoDiscountSearchRequestDto): Observable<IPagedResponse<PromoDiscountDataItem>> {
		const url = `${PricingServiceApi.promotionDiscount()}/search`;
		return this.post<PromoDiscountSearchRequestDto, IPagedResponse<PromoDiscountDataItem>>(
			url,
			searchRequest,
		);
	}

	// Get Bolt-On Data
	public getBoltOnData(searchRequest: PromoDiscountSearchRequestDto): Observable<IPagedResponse<PromoDiscountDataItem>> {
		const url = `${PricingServiceApi.boltOn()}/search`;
		return this.post<PromoDiscountSearchRequestDto, IPagedResponse<PromoDiscountDataItem>>(
			url,
			searchRequest,
		);
	}

	public getPromoServiceStatusList(): Observable<IPromoServiceStatus[]> {
		const url = `${PricingServiceApi.promoServiceStatusList()}/get/service-statuses`;
		return this.get<IPromoServiceStatus[]>(url);
	}

	public getPromoBillingStatusList(): Observable<IPromoBillingStatus[]> {
		const url = `${PricingServiceApi.promoBillingStatusList()}`;
		return this.get<IPromoBillingStatus[]>(url);
	}

	public deletePromotions(deleteRequest: PromoDiscountDeleteRequestDto) : Observable<void> {
		const url = `${PricingServiceApi.promotionDiscount()}/delete-list`;
		return this.post<PromoDiscountDeleteRequestDto, void>(
			url,
			deleteRequest,
		);
	}

	public deleteBoltOns(deleteRequest: PromoDiscountDeleteRequestDto) : Observable<void> {
		const url = `${PricingServiceApi.boltOn()}/delete-list`;
		return this.post<PromoDiscountDeleteRequestDto, void>(
			url,
			deleteRequest,
		);
	}

	// Get Promotion Discount by Id
	public getPromotionDiscountById(promoId: string): Observable<PromoDiscountItem> {
		const url = `${PricingServiceApi.promotionDiscount()}/${promoId}`;
		return this.get<PromoDiscountItem>(url);
	}

	// Get Bolt-On by Id
	public getBoltOnById(bolt_on_id: string): Observable<PromoDiscountItem> {
		const url = `${PricingServiceApi.boltOn()}/${bolt_on_id}`;
		return this.get<PromoDiscountItem>(url);
	}

	public createPromotionDiscount(promoDiscountItem : PromoDiscountItem): Observable<PromoDiscountItem> {
		const url = `${PricingServiceApi.promotionDiscount()}/create`;
		const object = instanceToPlain(promoDiscountItem);
		return this.post<object, PromoDiscountItem>(
			url,
			object,
		);
	}

	// Create Bolt-On
	public createBoltOn(promoDiscountItem : PromoDiscountItem): Observable<PromoDiscountItem> {
		const url = `${PricingServiceApi.boltOn()}/create`;
		const object = instanceToPlain(promoDiscountItem);
		return this.post<object, PromoDiscountItem>(
			url,
			object,
		);
	}

	public updatePromotionDiscount(promoDiscountItem : PromoDiscountItem): Observable<PromoDiscountItem> {
		const url = `${PricingServiceApi.promotionDiscount()}/update/${promoDiscountItem.promo_discount_id}`;
		const object = instanceToPlain(promoDiscountItem);
		return this.put<object, PromoDiscountItem>(
			url,
			object,
		);
	}

	// Update Bolt-On
	public updateBoltOn(promoDiscountItem : PromoDiscountItem): Observable<PromoDiscountItem> {
		const url = `${PricingServiceApi.boltOn()}/update/${promoDiscountItem.promo_discount_id}`;
		const object = instanceToPlain(promoDiscountItem);
		return this.put<object, PromoDiscountItem>(
			url,
			object,
		);
	}

	public bulkAddCustomerPromoDiscount(promoId: string, updateRequestDto: PromoDiscountBulkCustomerRequestDto): Observable<BulkCustomerResponseDto> {
		const url = `${PricingServiceApi.promotionDiscount()}/update/${promoId}/add-bulk-services`;
		return this.put<PromoDiscountBulkCustomerRequestDto, BulkCustomerResponseDto>(
			url,
			updateRequestDto,
		);
	}

	// Bulk Add Customer Bolt-On
	public bulkAddCustomerBoltOn(promoId: string, updateRequestDto: PromoDiscountBulkCustomerRequestDto): Observable<BulkCustomerResponseDto> {
		const url = `${PricingServiceApi.boltOn()}/update/${promoId}/add-bulk-services`;
		return this.put<PromoDiscountBulkCustomerRequestDto, BulkCustomerResponseDto>(
			url,
			updateRequestDto,
		);
	}

	public bulkDeleteCustomerPromoDiscount(promoId: string, updateRequestDto: PromoDiscountBulkCustomerRequestDto): Observable<BulkCustomerResponseDto> {
		const url = `${PricingServiceApi.promotionDiscount()}/update/${promoId}/remove-bulk-services`;
		return this.put<PromoDiscountBulkCustomerRequestDto, BulkCustomerResponseDto>(
			url,
			updateRequestDto,
		);
	}

	// Bulk Delete Customer Bolt-On
	public bulkDeleteCustomerBoltOn(promoId: string, updateRequestDto: PromoDiscountBulkCustomerRequestDto): Observable<BulkCustomerResponseDto> {
		const url = `${PricingServiceApi.boltOn()}/update/${promoId}/remove-bulk-services`;
		return this.put<PromoDiscountBulkCustomerRequestDto, BulkCustomerResponseDto>(
			url,
			updateRequestDto,
		);
	}

	public exportPromotionDiscountData(promoDiscountDataExportRequestDto : PromoDiscountDataExportRequestDto): Observable<HttpResponse<Blob>> {
		const url = `${PricingServiceApi.promotionDiscount()}/export`;
		return this.blobPost<PromoDiscountDataExportRequestDto>(
			url,
			promoDiscountDataExportRequestDto,
		);
	}

	// Export Bolt-On Data
	public exportBoltOnData(promoDiscountDataExportRequestDto : PromoDiscountDataExportRequestDto): Observable<HttpResponse<Blob>> {
		const url = `${PricingServiceApi.boltOn()}/export`;
		return this.blobPost<PromoDiscountDataExportRequestDto>(
			url,
			promoDiscountDataExportRequestDto,
		);
	}

	// Get Promotion Discount Report by Id
	public getPromotionDiscountReportById(promoId: string): Observable<PromoDiscountDataItem> {
		const url = `${PricingServiceApi.promotionDiscount()}/${promoId}/report`;
		return this.get<PromoDiscountDataItem>(url);
	}

	// Get Bolt-On Report by Id
	public getBoltOnReportById(promoId: string): Observable<PromoDiscountDataItem> {
		const url = `${PricingServiceApi.boltOn()}/${promoId}/report`;
		return this.get<PromoDiscountDataItem>(url);
	}

	public getPromotionReportSummaryData(searchRequest: PromoReportSearchRequestDto): Observable<IPagedResponse<PromoSummaryDto>> {
		const url = `${PricingServiceApi.promotionDiscountReport()}/promo-summary-info`;
		return this.post<PromoReportSearchRequestDto, IPagedResponse<PromoSummaryDto>>(
			url,
			searchRequest,
		);
	}

	public getBoltOnReportSummaryData(searchRequest: PromoReportSearchRequestDto): Observable<IPagedResponse<PromoSummaryDto>> {
		const url = `${PricingServiceApi.boltOnReport()}/promo-summary-info`;
		return this.post<PromoReportSearchRequestDto, IPagedResponse<PromoSummaryDto>>(
			url,
			searchRequest,
		);
	}

	// Service Promotions for the Promo Accordion
	public getServicePromoWithData(searchRequest: PromoReportSearchRequestDto): Observable<IPagedResponse<PromoSummaryWithDataDto>> {
		const url = `${PricingServiceApi.promotionDiscount()}/promotion-discounts-by-service`;
		return this.post<PromoReportSearchRequestDto, IPagedResponse<PromoSummaryWithDataDto>>(url, searchRequest);
	}

	// Get Bolt-On Service Promotions for the Promo Accordion
	public getServiceBoltOnWithData(searchRequest: PromoReportSearchRequestDto): Observable<IPagedResponse<PromoSummaryWithDataDto>> {
		const url = `${PricingServiceApi.boltOn()}/bolt-ons-by-service`;
		return this.post<PromoReportSearchRequestDto, IPagedResponse<PromoSummaryWithDataDto>>(url, searchRequest);
	}

	public getPromoSelectionList(searchRequest: PromoDiscountFilterListRequestDto) : Observable<IPromotionDiscount[]> {
		const url = `${PricingServiceApi.promotionDiscount()}/list`;
		return this.post<PromoDiscountFilterListRequestDto, IPromotionDiscount[]>(
			url,
			searchRequest,
		);
	}

	// Get Bolt-On Selection List
	public getBoltOnSelectionList(searchRequest: PromoDiscountFilterListRequestDto) : Observable<IPromotionDiscount[]> {
		const url = `${PricingServiceApi.boltOn()}/list`;
		return this.post<PromoDiscountFilterListRequestDto, IPromotionDiscount[]>(
			url,
			searchRequest,
		);
	}

	public getPromoSelectionListToAddService(serviceId: string) : Observable<IPromotionDiscountForService[]> {
		const url = `${PricingServiceApi.promotionDiscount()}/promotion-discounts-to-enlist?serviceid=${serviceId}`;
		return this.get<IPromotionDiscountForService[]>(url);
	}

	// Get Bolt-On Selection List to Add Service
	public getBoltOnSelectionListToAddService(serviceId: string) : Observable<IPromotionDiscountForService[]> {
		const url = `${PricingServiceApi.boltOn()}/bolt-ons-to-enlist?serviceid=${serviceId}`;
		return this.get<IPromotionDiscountForService[]>(url);
	}

	public getPromotionReportSummaryExportData(searchRequest: PromoReportSummaryDataExportRequestDto): Observable<HttpResponse<Blob>> {
		const url = `${PricingServiceApi.promotionDiscountReport()}/promo-summary-info/export`;
		return this.blobPost<PromoReportSummaryDataExportRequestDto>(
			url,
			searchRequest,
		);
	}

	// Get Bolt-On Report Summary Export Data
	public getBoltOnReportSummaryExportData(searchRequest: PromoReportSummaryDataExportRequestDto): Observable<HttpResponse<Blob>> {
		const url = `${PricingServiceApi.boltOnReport()}/promo-summary-info/export`;
		return this.blobPost<PromoReportSummaryDataExportRequestDto>(
			url,
			searchRequest,
		);
	}

	public getPromotionReportServiceSummaryData(searchRequest: PromoReportSearchRequestDto): Observable<IPagedResponse<PromoSummaryServiceDto>> {
		const url = `${PricingServiceApi.promotionDiscountReport()}/promo-service-summary-info`;
		return this.post<PromoReportSearchRequestDto, IPagedResponse<PromoSummaryServiceDto>>(
			url,
			searchRequest,
		);
	}

	// Get Bolt-On Report Service Summary Data
	public getBoltOnReportServiceSummaryData(searchRequest: PromoReportSearchRequestDto): Observable<IPagedResponse<PromoSummaryServiceDto>> {
		const url = `${PricingServiceApi.boltOnReport()}/promo-service-summary-info`;
		return this.post<PromoReportSearchRequestDto, IPagedResponse<PromoSummaryServiceDto>>(
			url,
			searchRequest,
		);
	}

	public getPromotionReportServiceSummaryExportData(searchRequest: PromoReportSummaryDataExportRequestDto): Observable<HttpResponse<Blob>> {
		const url = `${PricingServiceApi.promotionDiscountReport()}/promo-service-summary-info/export`;
		return this.blobPost<PromoReportSummaryDataExportRequestDto>(
			url,
			searchRequest,
		);
	}

	// Get Bolt-On Report Service Summary Export Data
	public getBoltOnReportServiceSummaryExportData(searchRequest: PromoReportSummaryDataExportRequestDto): Observable<HttpResponse<Blob>> {
		const url = `${PricingServiceApi.boltOnReport()}/promo-service-summary-info/export`;
		return this.blobPost<PromoReportSummaryDataExportRequestDto>(
			url,
			searchRequest,
		);
	}

	public getPromoReportSummaryServicePurchases(searchRequest: PromoReportSearchRequestDto): Observable<IPagedResponse<PromoSummaryServicePurchaseDto>> {
		const url = `${PricingServiceApi.promotionDiscountReport()}/promo-service-purchase-summary-info`;
		return this.post<PromoReportSearchRequestDto, IPagedResponse<PromoSummaryServicePurchaseDto>>(
			url,
			searchRequest,
		);
	}

	// Get Bolt-On Report Summary Service Purchases
	public getBoltOnReportSummaryServicePurchases(searchRequest: PromoReportSearchRequestDto): Observable<IPagedResponse<PromoSummaryServicePurchaseDto>> {
		const url = `${PricingServiceApi.boltOnReport()}/promo-service-purchase-summary-info`;
		return this.post<PromoReportSearchRequestDto, IPagedResponse<PromoSummaryServicePurchaseDto>>(
			url,
			searchRequest,
		);
	}

	public getPromoReportSummaryServicePurchasesExportData(searchRequest: PromoReportSummaryDataExportRequestDto): Observable<HttpResponse<Blob>> {
		const url = `${PricingServiceApi.promotionDiscountReport()}/promo-service-purchase-summary-info/export`;
		return this.blobPost<PromoReportSummaryDataExportRequestDto>(
			url,
			searchRequest,
		);
	}

	// Get Bolt-On Report Summary Service Purchases Export Data
	public getBoltOnReportSummaryServicePurchasesExportData(searchRequest: PromoReportSummaryDataExportRequestDto): Observable<HttpResponse<Blob>> {
		const url = `${PricingServiceApi.boltOnReport()}/promo-service-purchase-summary-info/export`;
		return this.blobPost<PromoReportSummaryDataExportRequestDto>(
			url,
			searchRequest,
		);
	}

	public getPromoReportSummaryServicePurchaseInfoData(
		searchRequest: PromoReportSearchRequestDto,
	): Observable<PromoServicePurchaseInfoDto> {
		const url = `${PricingServiceApi.promotionDiscountReport()}/promo-service-purchase-info`;
		return this.post<PromoReportSearchRequestDto, PromoServicePurchaseInfoDto>(
			url,
			searchRequest,
		);
	}

	// Get Bolt-On Report Summary Service Purchase Info Data
	public getBoltOnReportSummaryServicePurchaseInfoData(
		searchRequest: PromoReportSearchRequestDto,
	): Observable<PromoServicePurchaseInfoDto> {
		const url = `${PricingServiceApi.boltOnReport()}/promo-service-purchase-info`;
		return this.post<PromoReportSearchRequestDto, PromoServicePurchaseInfoDto>(
			url,
			searchRequest,
		);
	}

	public enlistServiceByPromoId(request : EnlistServiceByPromoIdRequestDto) : Observable<EligibilityResponseDto> {
		const url = `${PricingServiceApi.promotionDiscount()}/enlist`;
		return this.post<EnlistServiceByPromoIdRequestDto, EligibilityResponseDto>(
			url,
			request,
		);
	}

	// Enlist Service by Bolt-On Id
	public enlistServiceByBoltOnId(request : EnlistServiceByPromoIdRequestDto) : Observable<EligibilityResponseDto> {
		const url = `${PricingServiceApi.boltOn()}/enlist`;
		return this.post<EnlistServiceByPromoIdRequestDto, EligibilityResponseDto>(
			url,
			request,
		);
	}

	public getPromoExhaustReasons() : Observable<IPromoRemovalReason[]> {
		const url = `${PricingServiceApi.promotionDiscount()}/exhaust-reasons`;
		return this.get<IPromoRemovalReason[]>(url);
	}

	public getBoltOnExhaustReasons() : Observable<IPromoRemovalReason[]> {
		const url = `${PricingServiceApi.boltOn()}/exhaust-reasons`;
		return this.get<IPromoRemovalReason[]>(url);
	}

	public removePromoFromService(request : RemovePromoServiceRequestDto) : Observable<void> {
		const url = `${PricingServiceApi.promotionDiscount()}/exhaust`;
		return this.post<RemovePromoServiceRequestDto, void>(
			url,
			request,
		); 
	}

	// Remove Bolt-On from Service
	public removeBoltOnFromService(request : RemovePromoServiceRequestDto) : Observable<void> {
		const url = `${PricingServiceApi.boltOn()}/exhaust`;
		return this.post<RemovePromoServiceRequestDto, void>(
			url,
			request,
		); 
	}

}
